import React, { useState, useEffect, useCallback } from 'react';
// ✅ Stats (las comentamos/feature-flag) — NO importamos si está deshabilitado
// import RevenueChart from './components/RevenueChart';
// import ClientLeaderboard from './components/ClientLeaderboard';
// import ClientPortfolioMatrix from './components/ClientPortfolioMatrix';

import FilterControls from './components/FilterControls';
import NewClientModal from './components/NewClientModal';
import ClientSuperSearch from './components/ClientSuperSearch';
import EditClientModal from './components/EditClientModal';

// ✅ CORREGIDO: Subimos dos niveles (../../) para llegar a src/utils/supabase
import { supabase } from '../../utils/supabase';

// ✅ Feature flag: estadísticas inferiores (Ingresos/Top/Portafolio)
const ENABLE_BOTTOM_STATS = false;

const ClientAnalytics = () => {
  const [selectedSegment, setSelectedSegment] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('ytd');
  const [comparisonMode, setComparisonMode] = useState(false);
  const [lastUpdated, setLastUpdated] = useState('');
  const [newClientOpen, setNewClientOpen] = useState(false);

  const [editClientOpen, setEditClientOpen] = useState(false);
  const [clientToEdit, setClientToEdit] = useState(null);

  // ✅ NUEVO: últimos 12 clientes + siguiente sugerido (DB)
  const [latestClients, setLatestClients] = useState([]);
  const [nextClientNumber, setNextClientNumber] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [latestErr, setLatestErr] = useState('');

  useEffect(() => {
    const now = new Date();
    const timeString = now?.toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
    setLastUpdated(timeString);
  }, []);

  const refreshLatestClientsAndNext = useCallback(async () => {
    try {
      setLoadingLatest(true);
      setLatestErr('');

      // 1) Siguiente número sugerido desde DB (RPC)
      const { data: nextNum, error: nextErr } = await supabase.rpc('get_next_client_number');
      if (nextErr) throw nextErr;
      setNextClientNumber(nextNum ?? null);

      // 2) Últimos 12 clientes creados
      const { data: latest, error: latestError } = await supabase
        .from('clients')
        .select('id,name,cuit,tax_id,client_number,branch_no,branch_name,address,created_at,billing_email_1,billing_email_2')
        .order('created_at', { ascending: false })
        .limit(12);

      if (latestError) throw latestError;
      setLatestClients(latest || []);
    } catch (e) {
      console.error('[ClientAnalytics] refreshLatestClientsAndNext', e);
      setLatestErr(e?.message || 'No se pudo cargar los últimos clientes');
    } finally {
      setLoadingLatest(false);
    }
  }, []);

  useEffect(() => {
    refreshLatestClientsAndNext();
  }, [refreshLatestClientsAndNext]);

  const handleExport = (format) => {
    console.log(`Exportando datos en formato ${format}`);
    alert(`Exportando análisis en formato ${format?.toUpperCase()}`);
  };

  const openEditClient = (client) => {
    setClientToEdit(client);
    setEditClientOpen(true);
  };

  const formatClientCode = (c) => {
    const cn = c?.client_number ?? '';
    const bn = c?.branch_no ?? null;
    if (!cn) return '—';
    return bn ? `${cn} ${bn}` : `${cn}`;
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('es-ES', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return '—';
    }
  };

  // ✅ Import dinámico solo si se habilitan stats (evita warnings y carga innecesaria)
  const RevenueChart = React.useMemo(() => (
    ENABLE_BOTTOM_STATS ? React.lazy(() => import('./components/RevenueChart')) : null
  ), []);
  const ClientLeaderboard = React.useMemo(() => (
    ENABLE_BOTTOM_STATS ? React.lazy(() => import('./components/ClientLeaderboard')) : null
  ), []);
  const ClientPortfolioMatrix = React.useMemo(() => (
    ENABLE_BOTTOM_STATS ? React.lazy(() => import('./components/ClientPortfolioMatrix')) : null
  ), []);

  return (
    <div className="min-h-screen bg-background">
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* Título + Acciones */}
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">Análisis de Clientes</h1>
              <p className="text-muted-foreground">
                Seguimiento de relaciones con clientes, rendimiento de ingresos y oportunidades de crecimiento
              </p>
            </div>

            <div className="flex items-center gap-2">
              {/* ✅ NUEVO: Siguiente sugerido visible arriba */}
              <div className="hidden md:flex items-center gap-2 px-3 py-2 rounded-md border border-white/10 bg-white/5">
                <span className="text-xs text-muted-foreground">Siguiente N° sugerido:</span>
                <span className="text-sm font-semibold text-foreground">
                  {nextClientNumber ?? '—'}
                </span>
              </div>

              <button
                onClick={() => setNewClientOpen(true)}
                className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 shadow-sm"
              >
                + Nuevo Cliente
              </button>
            </div>
          </div>

          {/* ✅ NUEVO: Últimos 12 clientes creados */}
          <div className="mb-6 rounded-2xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-lg font-semibold text-foreground">Últimos 12 clientes creados</div>
                <div className="text-xs text-muted-foreground">
                  Te sirve para ver rápido el último número real y evitar errores al cargar.
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="text-sm text-muted-foreground">
                  Siguiente sugerido:{' '}
                  <span className="text-foreground font-semibold">
                    {nextClientNumber ?? '—'}
                  </span>
                </div>
                <button
                  onClick={refreshLatestClientsAndNext}
                  className="px-3 py-2 rounded-md border border-white/15 bg-white/5 text-sm hover:bg-white/10"
                  disabled={loadingLatest}
                  title="Refrescar"
                >
                  {loadingLatest ? 'Actualizando…' : 'Refrescar'}
                </button>
              </div>
            </div>

            {latestErr ? (
              <div className="mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
                {latestErr}
              </div>
            ) : null}

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-muted-foreground">
                  <tr className="border-b border-white/10">
                    <th className="py-2 pr-4 text-left whitespace-nowrap">Fecha</th>
                    <th className="py-2 pr-4 text-left whitespace-nowrap">Código</th>
                    <th className="py-2 pr-4 text-left">Cliente</th>
                    <th className="py-2 pr-4 text-left whitespace-nowrap">CUIT</th>
                    <th className="py-2 pr-4 text-left">Sucursal / Planta</th>
                    <th className="py-2 text-right whitespace-nowrap">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {(latestClients || []).map((c) => (
                    <tr key={c.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 pr-4 whitespace-nowrap text-foreground/80">
                        {formatDate(c.created_at)}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap font-semibold text-foreground">
                        {formatClientCode(c)}
                      </td>
                      <td className="py-2 pr-4 text-foreground">
                        <div className="font-medium">{c.name || '—'}</div>
                        {c.address ? (
                          <div className="text-xs text-muted-foreground line-clamp-1">{c.address}</div>
                        ) : null}
                      </td>
                      <td className="py-2 pr-4 whitespace-nowrap text-foreground/80">
                        {c.cuit || c.tax_id || '—'}
                      </td>
                      <td className="py-2 pr-4 text-foreground/80">
                        {c.branch_name || '—'}
                      </td>
                      <td className="py-2 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditClient(c)}
                          className="px-3 py-1.5 rounded-md border border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}

                  {!loadingLatest && (!latestClients || latestClients.length === 0) ? (
                    <tr>
                      <td colSpan={6} className="py-4 text-center text-muted-foreground">
                        Sin datos para mostrar.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          {/* Super Buscador + Editor */}
          <div className="mb-6">
            <ClientSuperSearch onEditClient={openEditClient} />
          </div>

          {/* Filtros superiores */}
          <FilterControls
            selectedSegment={selectedSegment}
            onSegmentChange={setSelectedSegment}
            selectedPeriod={selectedPeriod}
            onPeriodChange={setSelectedPeriod}
            comparisonMode={comparisonMode}
            onComparisonToggle={() => setComparisonMode(!comparisonMode)}
            onExport={handleExport}
            lastUpdated={lastUpdated}
          />

          {/* ✅ ESTADÍSTICAS INFERIORES (comentadas por flag) */}
          {ENABLE_BOTTOM_STATS ? (
            <React.Suspense
              fallback={<div className="mt-8 text-sm text-muted-foreground">Cargando estadísticas…</div>}
            >
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8">
                <div className="xl:col-span-2">
                  <RevenueChart />
                </div>
                <div className="xl:col-span-1">
                  <ClientLeaderboard />
                </div>
              </div>

              <div className="mb-8">
                <ClientPortfolioMatrix />
              </div>
            </React.Suspense>
          ) : (
            <div className="mt-6 text-xs text-muted-foreground/70">
              {/* Estadísticas inferiores deshabilitadas (ENABLE_BOTTOM_STATS=false) */}
            </div>
          )}

          {/* Modal de alta de cliente */}
          <NewClientModal
            isOpen={newClientOpen}
            onClose={() => setNewClientOpen(false)}
            suggestedNextNumber={nextClientNumber}
            onCreated={() => {
              refreshLatestClientsAndNext();
            }}
          />

          {/* Modal editor */}
          <EditClientModal
            isOpen={editClientOpen}
            client={clientToEdit}
            onClose={() => {
              setEditClientOpen(false);
              setClientToEdit(null);
            }}
            onSaved={(updated) => {
              // ✅ 1) Actualiza el objeto en memoria (modal + si está en la tabla)
              setClientToEdit(updated);

              setLatestClients((prev) => {
                if (!prev || prev.length === 0) return prev;
                const idx = prev.findIndex((x) => x.id === updated?.id);
                if (idx === -1) return prev; // si no está en los últimos 12, no tocamos nada
                const next = [...prev];
                next[idx] = { ...next[idx], ...updated };
                return next;
              });

              // ✅ 2) Cerramos modal
              setEditClientOpen(false);

              // ✅ 3) Refrescamos sugerido + últimos 12 desde DB
              refreshLatestClientsAndNext();
            }}
          />
        </div>
      </main>
    </div>
  );
};

export default ClientAnalytics;
